// ---------------------------------------------------------------------------
// tabs — vanilla click/keyboard tab switching, ported from the legacy
// `data-section-key` key-components UI in jqueryEvents.js.
//
// Markup contract (see html/ProductPage.html):
//   <div data-tabs>
//     <button role="tab" data-tab="0">…</button>          (the label list)
//     <div role="tabpanel" data-tab-panel="0">…</div>     (the media panel)
//   </div>
// A tab and its panel are paired by matching `data-tab` / `data-tab-panel`.
// The active pair carries `.active`; ARIA state mirrors it. Falling back to
// no-JS, every panel is visible (CSS only hides the inactive ones once this
// module has run and tagged the container with `.tabs-ready`).
// ---------------------------------------------------------------------------

const activate = (
	tabs: HTMLElement[],
	panels: HTMLElement[],
	key: string,
	focus: boolean,
): void => {
	for (const tab of tabs) {
		const selected = tab.dataset.tab === key;
		// `active`, not `is-active`: the vendored stylesheet keys tab state off
		// `.title.active` / `.product__image-container.active` / `.content.active`
		// (legacy/pageProduct.scss). Emitting `is-active` left every feature panel
		// at `display: none`, so the product pages showed their tab labels and
		// never the image beside them. The only `.is-active` rules in the build
		// belong to the vendored hamburger library.
		tab.classList.toggle('active', selected);
		tab.setAttribute('aria-selected', selected ? 'true' : 'false');
		tab.setAttribute('tabindex', selected ? '0' : '-1');
		if (selected && focus) tab.focus();
	}
	for (const panel of panels) {
		const selected = panel.dataset.tabPanel === key;
		panel.classList.toggle('active', selected);
		panel.toggleAttribute('hidden', !selected);
		// A feature panel may hold a video instead of a still, and these groups
		// run to a dozen panels. The template marks them `preload="none"` and
		// autoplays only the first, so playback has to follow the selection or
		// every panel after the first stays on its poster frame. `play()` rejects
		// when the browser blocks autoplay — the poster is the fallback, so the
		// rejection is nothing to handle.
		for (const media of panel.querySelectorAll('video')) {
			if (selected) void media.play().catch(() => {});
			else media.pause();
		}
	}
};

const initGroup = (group: HTMLElement): void => {
	const tabs = Array.from(group.querySelectorAll<HTMLElement>('[data-tab]'));
	const panels = Array.from(group.querySelectorAll<HTMLElement>('[data-tab-panel]'));
	if (!tabs.length || !panels.length) return;

	tabs.forEach((tab) => {
		tab.addEventListener('click', () => {
			const key = tab.dataset.tab;
			if (key != null) activate(tabs, panels, key, false);
		});
	});

	// Roving-tabindex arrow-key navigation across the tab list.
	group.addEventListener('keydown', (event) => {
		const current = tabs.findIndex((t) => t === document.activeElement);
		if (current === -1) return;
		let next = current;
		if (event.key === 'ArrowDown' || event.key === 'ArrowRight') next = current + 1;
		else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') next = current - 1;
		else if (event.key === 'Home') next = 0;
		else if (event.key === 'End') next = tabs.length - 1;
		else return;
		event.preventDefault();
		next = (next + tabs.length) % tabs.length;
		const key = tabs[next].dataset.tab;
		if (key != null) activate(tabs, panels, key, true);
	});

	// Pick the pre-marked active tab (template sets `.active` on the first).
	const initial = tabs.find((t) => t.classList.contains('active')) ?? tabs[0];
	const key = initial.dataset.tab ?? '0';
	activate(tabs, panels, key, false);
	group.classList.add('tabs-ready');
};

export const initTabs = (): void => {
	const groups = document.querySelectorAll<HTMLElement>('[data-tabs]');
	for (const group of groups) initGroup(group);
};
